import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { ArrowLeft, FileText, Edit, Trash2 } from 'lucide-react';
import { usePermissions } from '@contexts/AuthContext';
import { contractService, assessmentService } from '@services';
import PageLayout from '@components/layout/PageLayout';
import Button from '@components/common/Button';
import DetailPageHeader from '@components/common/DetailPageHeader';
import Badge from '@components/common/Badge';
import EntityActivity from '@components/common/EntityActivity';
import RoleAssignmentCard from '@components/common/RoleAssignmentCard';
import MultiRoleAssignmentModal from '@components/common/MultiRoleAssignmentModal';
import ConfirmationModal from '@components/common/modals/ConfirmationModal';
import { ContractInfo, ContractAssessments, ContractMeetings } from '@components/features/contracts';
import { AttachmentSection } from '@components/features/attachments';
import SupplierSearchModal from '@components/features/contracts/SupplierSearchModal';
import ReassociateConfirmationModal from '@components/features/contracts/ReassociateConfirmationModal';
import { ContractFormModal } from '@components/features/contracts';
import { LoadingState } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import type { Supplier, ApiError } from '@app-types';
import { showToast } from '@utils/toast';
import { getApiErrorMessage, getApiErrorStatus } from '@utils/apiError';
import { contractStatusToVariant } from '@utils/badgeMappers';
import { getContractStatusLabel } from '@utils/referenceCatalog';
import styles from './ContractDetailPage.module.css';

const ContractDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const contractId = Number(id);
  const [showManagerModal, setShowManagerModal] = React.useState(false);
  const [showOwnerModal, setShowOwnerModal] = React.useState(false);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  // Supplier reassociation state
  const [showSupplierSearchModal, setShowSupplierSearchModal] = React.useState(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [selectedSupplier, setSelectedSupplier] = React.useState<Supplier | null>(null);
  const [reassociationError, setReassociationError] = React.useState<string | null>(null);

  // Fetch contract details
  const {
    data: contract,
    isLoading,
    error
  } = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => contractService.getContract(contractId),
    enabled: !!id && Number.isFinite(contractId),
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true // Refetch when window regains focus
  });

  // Fetch assessments for this contract (filtered on backend)
  const { data: assessmentResponse } = useQuery({
    queryKey: ['assessments', 'contract', contractId],
    queryFn: () =>
      assessmentService.getAssessments({
        contract_id: contractId,
        limit: 100,
        offset: 0
      }),
    enabled: !!id,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true // Refetch when window regains focus
  });

  const { data: contractMeetings = [], isLoading: meetingsLoading } = useQuery({
    queryKey: ['meetings', 'contract', contractId],
    queryFn: () => contractService.getContractMeetings(contractId),
    enabled: !!id && Number.isFinite(contractId),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true
  });

  // Assessments already filtered by contract_id on backend
  const contractAssessments = React.useMemo(() => {
    return assessmentResponse?.data || [];
  }, [assessmentResponse?.data]);

  // Count active assessments (not Final or Cancelled)
  const activeAssessmentsCount = React.useMemo(() => {
    return contractAssessments.filter(
      (assessment) => assessment.assessment_status !== 'Final' && assessment.assessment_status !== 'Cancelled'
    ).length;
  }, [contractAssessments]);

  const handleBack = () => {
    navigate(-1); // Go back to previous page in browser history
  };

  const handleAssignManager = () => {
    setShowManagerModal(true);
  };

  const handleAssignOwner = () => {
    setShowOwnerModal(true);
  };

  const handleRefreshData = () => {
    queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
    queryClient.invalidateQueries({ queryKey: ['contracts'] });
    queryClient.invalidateQueries({ queryKey: ['audit-history', 'contract', contractId] });
  };

  const handleViewAssessment = (assessmentId: number) => {
    navigate(`/assessments/${assessmentId}`);
  };

  const handleViewMeeting = (meetingId: number) => {
    navigate(`/contract-assessment-meetings/${meetingId}`);
  };

  const handleViewSupplier = (supplierId: number) => {
    navigate(`/suppliers/${supplierId}`);
  };

  const deleteMutation = useMutation({
    mutationFn: (id: number) => contractService.deleteContract(id),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: ['contract', contractId] });
      await queryClient.refetchQueries({ queryKey: ['contracts'] });
      showToast.success('Contract deleted successfully');
      navigate('/contracts');
    },
    onError: (error: ApiError) => {
      setShowDeleteModal(false);
      showToast.error(getApiErrorMessage(error, 'Failed to delete contract'));
    }
  });

  // Supplier reassociation mutation
  const reassociateMutation = useMutation({
    mutationFn: (supplierId: number) => contractService.reassociateContractSupplier(contractId, supplierId),
    onSuccess: () => {
      // Close modals
      setShowConfirmModal(false);
      setShowSupplierSearchModal(false);

      // Reset state
      setSelectedSupplier(null);
      setReassociationError(null);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['audit-history', 'contract', contractId] });

      // Invalidate supplier queries (status may have changed for both old and new suppliers)
      queryClient.invalidateQueries({ queryKey: ['suppliers'] }); // Supplier list page
      queryClient.invalidateQueries({ queryKey: ['supplier'] }); // All individual supplier pages
      queryClient.invalidateQueries({ queryKey: ['supplier-contracts'] }); // Supplier contracts

      // Show success message
      showToast.success('Contract supplier reassociated successfully');
    },
    onError: (error: ApiError) => {
      setReassociationError(getApiErrorMessage(error, 'Failed to reassociate contract supplier'));
    }
  });

  const handleChangeSupplier = () => {
    setShowSupplierSearchModal(true);
    setReassociationError(null);
  };

  const handleSupplierSelect = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowSupplierSearchModal(false);
    setShowConfirmModal(true);
  };

  const handleConfirmReassociation = () => {
    if (selectedSupplier) {
      reassociateMutation.mutate(selectedSupplier.id);
    }
  };

  const handleCloseConfirmModal = () => {
    if (!reassociateMutation.isPending) {
      setShowConfirmModal(false);
      setReassociationError(null);
    }
  };

  if (isLoading) {
    return (
      <PageLayout>
        <LoadingState message="Loading contract details..." spinnerSize="large" />
      </PageLayout>
    );
  }

  if (error) {
    const isForbidden = getApiErrorStatus(error) === 403;
    return (
      <PageLayout>
        <ErrorState
          title={isForbidden ? 'Access Denied' : 'Contract Not Found'}
          message={
            isForbidden
              ? 'You do not have permission to view this contract.'
              : "The contract you're looking for could not be found."
          }
          action={
            <Button variant="primary" onClick={handleBack}>
              <ArrowLeft size={16} />
              Go Back
            </Button>
          }
        />
      </PageLayout>
    );
  }

  if (!contract) {
    return null;
  }

  const canAssignManager = permissions.isAdmin;
  const canAssignOwner = permissions.isAdmin;
  const canChangeSupplier = permissions.isAdmin || permissions.isScheduler;
  const hasLinkedRecords = contractAssessments.length > 0 || contractMeetings.length > 0;

  return (
    <PageLayout>
      <DetailPageHeader
        icon={<FileText size={24} />}
        title={contract.contract_number}
        subtitle={contract.name}
        backLabel="Go Back"
        onBack={handleBack}
        actions={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Badge variant={contractStatusToVariant(contract.status)}>{getContractStatusLabel(contract.status)}</Badge>

            {permissions.canEditContracts && (
              <Button variant="secondary" size="sm" onClick={() => setShowEditModal(true)}>
                <Edit size={16} />
                Edit
              </Button>
            )}

            {permissions.canDeleteContracts && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowDeleteModal(true)}
                disabled={hasLinkedRecords}
                title={
                  hasLinkedRecords
                    ? 'Cannot delete contract with linked meetings or assessments'
                    : 'Delete contract'
                }
              >
                <Trash2 size={16} />
                Delete
              </Button>
            )}
          </div>
        }
      />

      <div className="detail-content">
        <div className={styles.detailMain}>
          <ContractInfo
            contract={contract}
            assessmentsCount={contractAssessments.length}
            activeAssessmentsCount={activeAssessmentsCount}
            onViewSupplier={handleViewSupplier}
            onChangeSupplier={handleChangeSupplier}
            canChangeSupplier={canChangeSupplier}
          />

          {/* Contract Manager Assignment Section */}
          {canAssignManager && (
            <RoleAssignmentCard
              title="Contract Manager"
              currentAssignee={contract.contract_managers?.map((m) => m.name).join(', ') || undefined}
              currentAssigneeId={contract.contract_managers?.[0]?.id || null}
              description="Contract managers are responsible for overseeing contract performance, managing assessments, and ensuring compliance."
              buttonText="Assign Manager"
              onAssign={handleAssignManager}
              canAssign={canAssignManager}
            />
          )}

          {/* Contract Owner Assignment Section */}
          {canAssignOwner && (
            <RoleAssignmentCard
              title="Contract Owner"
              currentAssignee={contract.contract_owners?.map((o) => o.name).join(', ') || undefined}
              currentAssigneeId={contract.contract_owners?.[0]?.id || null}
              description="Contract owners are the primary business owners responsible for this contract."
              buttonText="Assign Owner"
              onAssign={handleAssignOwner}
              canAssign={canAssignOwner}
            />
          )}

          <ContractMeetings
            meetings={contractMeetings}
            isLoading={meetingsLoading}
            onViewMeeting={handleViewMeeting}
          />

          <ContractAssessments
            assessments={contractAssessments}
            onViewAssessment={handleViewAssessment}
            hideProgress={permissions.isProgramLead && !permissions.isAdminStrict}
          />

          <AttachmentSection
            entityType="contract"
            entityId={contract.id}
            canManage={permissions.isAdmin || permissions.isContractRole || permissions.isScheduler}
          />

          {permissions.canAccessNotes && (
            <EntityActivity entityType="contract" entityId={contract.id} title="Contract Activity" />
          )}
        </div>
      </div>

      {/* Manager Assignment Modal */}
      {showManagerModal && (
        <MultiRoleAssignmentModal
          contractNumber={contract.contract_number}
          supplierName={contract.supplier_name}
          roleType="manager"
          currentAssigneeIds={contract.contract_managers?.map((m) => m.id) || []}
          onClose={() => setShowManagerModal(false)}
          onAssigned={async (managerIds) => {
            await contractService.updateContract(contract.id, { contract_managers: managerIds });
            handleRefreshData();
          }}
        />
      )}

      {/* Owner Assignment Modal */}
      {showOwnerModal && (
        <MultiRoleAssignmentModal
          contractNumber={contract.contract_number}
          supplierName={contract.supplier_name}
          roleType="owner"
          currentAssigneeIds={contract.contract_owners?.map((o) => o.id) || []}
          onClose={() => setShowOwnerModal(false)}
          onAssigned={async (ownerIds) => {
            await contractService.updateContract(contract.id, { contract_owners: ownerIds });
            handleRefreshData();
          }}
        />
      )}

      {/* Supplier Search Modal */}
      <SupplierSearchModal
        isOpen={showSupplierSearchModal}
        onClose={() => setShowSupplierSearchModal(false)}
        currentSupplierId={contract.supplier_id}
        onSupplierSelect={handleSupplierSelect}
      />

      {/* Reassociation Confirmation Modal */}
      {selectedSupplier && (
        <ReassociateConfirmationModal
          isOpen={showConfirmModal}
          onClose={handleCloseConfirmModal}
          onConfirm={handleConfirmReassociation}
          contractNumber={contract.contract_number}
          oldSupplierName={contract.supplier_name || 'Unknown'}
          newSupplierName={selectedSupplier.name}
          isLoading={reassociateMutation.isPending}
          error={reassociationError}
        />
      )}

      {/* Edit Contract Modal */}
      <ContractFormModal
        mode="edit"
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleRefreshData}
        contract={contract}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => deleteMutation.mutate(contractId)}
        title="Delete Contract"
        message={`Are you sure you want to delete "${contract.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="danger"
        isProcessing={deleteMutation.isPending}
      />
    </PageLayout>
  );
};

export default ContractDetailPage;
